import {
  AiConversation,
  AiConversationMetadata,
  isAiConversation,
  isAiConversationMetadata,
} from '../models/ai-chat.model';
import { IComment } from '../models/comment.model';
import { IProduct } from '../models/product.model';
import { Project } from '../models/project.model';
import { Recipe } from '../models/recipe.model';
import {
  ProjectFileBatchSnapshot,
  isProjectFileBatchSnapshot,
} from '../models/project-file-batch.model';
import { isProduct } from './product.helpers';
import { isComment } from './comment.helpers';
import { isProject } from './recursive-element.helpers';
import { isRecipe } from './recipe.helpers';

export enum SocketEventName {
  ProjectCreate = 'project_create',
  ProjectUpdate = 'project_update',
  ProjectDelete = 'project_delete',
  RecipeCreate = 'recipe_create',
  RecipeUpdate = 'recipe_update',
  RecipeDelete = 'recipe_delete',
  ProductCreate = 'product_create',
  ProductUpdate = 'product_update',
  ProductDelete = 'product_delete',
  CommentCreate = 'comment_create',
  CommentUpdate = 'comment_update',
  CommentGroupUpdate = 'comment_group_update',
  CommentDelete = 'comment_delete',
  AiConversationUpdate = 'ai_conversation_update',
  AiConversationMetadataUpdate = 'ai_conversation_metadata_update',
  ProjectFileBatchUpdate = 'project_file_batch_update',
}

export type SocketEventDataType = {
  [SocketEventName.ProjectCreate]: Project;
  [SocketEventName.ProjectUpdate]: Project;
  [SocketEventName.ProjectDelete]: Project['id'];
  [SocketEventName.RecipeCreate]: Recipe;
  [SocketEventName.RecipeUpdate]: Recipe;
  // Soft-delete emits the full recipe (with deleted_at) so clients update in place.
  [SocketEventName.RecipeDelete]: Recipe;
  [SocketEventName.ProductCreate]: IProduct;
  [SocketEventName.ProductUpdate]: IProduct;
  [SocketEventName.ProductDelete]: IProduct['id'];
  [SocketEventName.CommentCreate]: IComment;
  [SocketEventName.CommentUpdate]: IComment;
  [SocketEventName.CommentGroupUpdate]: IComment[];
  [SocketEventName.CommentDelete]: IComment['id'];
  [SocketEventName.AiConversationUpdate]: AiConversation;
  [SocketEventName.AiConversationMetadataUpdate]: AiConversationMetadata;
  [SocketEventName.ProjectFileBatchUpdate]: ProjectFileBatchSnapshot;
};

export type SocketMetaData = {
  organization: string;
  sessionId: string;
};

export const isSocketEventData = <T extends SocketEventName>(
  name: T,
  data: unknown,
): data is SocketEventDataType[T] => {
  switch (name) {
    case SocketEventName.ProjectDelete:
      return typeof data === 'number';
    case SocketEventName.ProjectCreate:
    case SocketEventName.ProjectUpdate:
      return isProject(data);
    case SocketEventName.RecipeCreate:
    case SocketEventName.RecipeUpdate:
    case SocketEventName.RecipeDelete:
      return isRecipe(data);
    case SocketEventName.ProductCreate:
    case SocketEventName.ProductUpdate:
    case SocketEventName.ProductDelete:
      return isProduct(data);
    case SocketEventName.CommentGroupUpdate:
      return Array.isArray(data) && data.every(isComment);
    case SocketEventName.CommentCreate:
    case SocketEventName.CommentUpdate:
    case SocketEventName.CommentDelete:
      return isComment(data);
    case SocketEventName.AiConversationUpdate:
      return isAiConversation(data);
    case SocketEventName.AiConversationMetadataUpdate:
      return isAiConversationMetadata(data);
    case SocketEventName.ProjectFileBatchUpdate:
      return isProjectFileBatchSnapshot(data);
  }
  throw new Error(`Invalid socket event data type: "${name}"`);
};

export const validateSocketEventData = <T extends SocketEventName>(
  name: T,
  data: unknown,
): SocketEventDataType[T] => {
  if (isSocketEventData(name, data)) {
    return data;
  }
  throw new Error(`Invalid socket event data type: "${name}"`);
};
